/* PageFlow interface. Every rule is scoped to .pf-app (or the drag ghost) and resists the host's CSS. */
.pf-app {
    --pf-height: 100vh;
    --pf-bg: #f4f3fa;
    --pf-panel: #ffffff;
    --pf-panel-2: #f8f7fd;
    --pf-border: #e5e3f1;
    --pf-text: #1e1b3a;
    --pf-muted: #6b6884;
    --pf-accent: #6d5efc;
    --pf-accent-strong: #5544f0;
    --pf-accent-soft: #efedff;
    --pf-danger: #e5484d;
    --pf-success: #12a36a;
    --pf-warning: #d98e04;
    --pf-radius: 10px;
    --pf-shadow: 0 1px 2px rgba(30, 27, 58, .06), 0 8px 24px rgba(30, 27, 58, .08);
    --pf-left: 264px;
    --pf-right: 300px;
    container-type: inline-size;
    position: relative;
    display: flex;
    flex-direction: column;
    height: var(--pf-height);
    min-height: 420px;
    border: 1px solid var(--pf-border);
    border-radius: 12px;
    overflow: hidden;
    background: var(--pf-bg);
    color: var(--pf-text);
    font: 400 13px/1.4 system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    text-align: left;
    -webkit-font-smoothing: antialiased;
}
.pf-app[data-mode="compact"] { --pf-height: 760px; --pf-left: 236px; --pf-right: 280px; }
.pf-app[data-theme="dark"] {
    --pf-bg: #121124;
    --pf-panel: #1b1a30;
    --pf-panel-2: #22213a;
    --pf-border: #2e2c4a;
    --pf-text: #ecebff;
    --pf-muted: #a3a0c2;
    --pf-accent: #8b7dff;
    --pf-accent-strong: #a397ff;
    --pf-accent-soft: #2a2550;
    --pf-shadow: 0 1px 2px rgba(0, 0, 0, .3), 0 8px 24px rgba(0, 0, 0, .35);
}
.pf-app *,
.pf-app *::before,
.pf-app *::after { box-sizing: border-box; }
.pf-app :where(p, ul, ol, h1, h2, h3, h4, label, figure) { margin: 0; padding: 0; }
.pf-app :where(button, input, select, textarea) { font: inherit; color: inherit; letter-spacing: normal; margin: 0; }
.pf-app :where(button) { background: none; border: 0; padding: 0; cursor: pointer; text-transform: none; }
.pf-app :where(button:disabled) { cursor: default; opacity: .4; }
.pf-app :where(button, input, select, textarea, summary, [tabindex]):focus-visible { outline: 2px solid var(--pf-accent); outline-offset: 1px; }
.pf-app :where(label) { display: block; }
/* Defensive reset: global rules of the host page (main { margin: 0 auto }, ul { padding-left }, input { width: 100% },
   button { background }, * { transition }...) never reach the interface. Its own rules come after, more specific. */
.pf-app :where(div, span, p, ul, ol, li, h1, h2, h3, h4, label, figure, details, summary, dialog, kbd, code, small, strong, img, svg, section, nav, main, aside, header, footer) {
    max-width: none;
    float: none;
    text-transform: none;
    letter-spacing: normal;
    transition: none;
    animation: none;
}
.pf-app :where(div, span, p, ul, ol, li, h1, h2, h3, h4, label, figure, details, summary, kbd, code, small, strong, section, nav, main, aside, header, footer) { margin: 0; padding: 0; }
.pf-app :where(ul, ol, li) { list-style: none; }
.pf-app :where(input, select, textarea) { width: auto; height: auto; min-width: 0; min-height: 0; max-width: 100%; box-shadow: none; border-radius: 0; }
.pf-app :where(button) { width: auto; height: auto; min-width: 0; min-height: 0; box-shadow: none; border-radius: 0; line-height: normal; }
.pf-app [hidden] { display: none !important; }
.pf-app .pf-icon { display: inline-grid; place-items: center; flex: none; width: 18px; height: 18px; }
.pf-app .pf-icon svg { display: block; width: 100%; height: 100%; }
.pf-app.pf-fullscreen { position: fixed; inset: 0; z-index: 2147482000; height: 100vh !important; height: 100dvh !important; border: 0; border-radius: 0; }
html.pf-has-fullscreen,
html.pf-has-fullscreen body { overflow: hidden !important; }

/* ---- Top bar ---- */
.pf-app .pf-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 50px;
    padding: 6px 8px;
    background: var(--pf-panel);
    border-bottom: 1px solid var(--pf-border);
    position: relative;
    z-index: 3;
}
.pf-app .pf-bar-group { display: flex; align-items: center; gap: 2px; }
.pf-app .pf-devices { padding: 3px; border-radius: 9px; background: var(--pf-bg); }
.pf-app .pf-tool { display: grid; place-items: center; width: 34px; height: 34px; border-radius: 8px; color: var(--pf-muted); transition: background .15s, color .15s; }
.pf-app .pf-tool .pf-icon { width: 19px; height: 19px; }
.pf-app .pf-tool:hover:not(:disabled) { background: var(--pf-accent-soft); color: var(--pf-accent); }
.pf-app .pf-tool:active:not(:disabled) { transform: scale(.92); }
.pf-app .pf-tool { transition: background .15s, color .15s, transform .08s; }
.pf-app .pf-tool.pf-on { background: var(--pf-accent); color: #fff; }
.pf-app .pf-devices .pf-tool { width: 32px; height: 28px; border-radius: 6px; }
.pf-app .pf-devices .pf-tool.pf-on { background: var(--pf-panel); color: var(--pf-accent); box-shadow: 0 1px 3px rgba(30, 27, 58, .15); }
.pf-app .pf-action { display: inline-flex; align-items: center; gap: 6px; height: 34px; margin-left: 6px; padding: 0 14px; border-radius: 8px; background: var(--pf-bg); font-weight: 600; }
.pf-app .pf-action:hover { background: var(--pf-accent-soft); color: var(--pf-accent); }
.pf-app .pf-action-primary { background: var(--pf-accent); color: #fff; }
.pf-app .pf-action-primary:hover { background: var(--pf-accent-strong); color: #fff; }
.pf-app:not(.pf-narrow) [data-tool="insert"]:not(.pf-on),
.pf-app:not(.pf-narrow) [data-tool="settings"]:not(.pf-on) { color: var(--pf-muted); }
.pf-app:not(.pf-narrow) [data-tool="insert"].pf-on,
.pf-app:not(.pf-narrow) [data-tool="settings"].pf-on { background: var(--pf-accent-soft); color: var(--pf-accent); }
.pf-app.pf-hide-left:not(.pf-narrow) .pf-left,
.pf-app.pf-hide-right:not(.pf-narrow) .pf-right { display: none; }
.pf-app.pf-hide-left:not(.pf-narrow) .pf-main { grid-template-columns: minmax(0, 1fr) var(--pf-right); }
.pf-app.pf-hide-right:not(.pf-narrow) .pf-main { grid-template-columns: var(--pf-left) minmax(0, 1fr); }
.pf-app.pf-hide-left.pf-hide-right:not(.pf-narrow) .pf-main { grid-template-columns: minmax(0, 1fr); }

/* ---- Main area ---- */
.pf-app .pf-main { position: relative; flex: 1; display: grid; grid-template-columns: var(--pf-left) minmax(0, 1fr) var(--pf-right); min-height: 0; }
.pf-app .pf-left,
.pf-app .pf-right { min-height: 0; overflow-y: auto; overflow-x: hidden; background: var(--pf-panel); scrollbar-width: thin; scrollbar-color: color-mix(in srgb, var(--pf-muted) 40%, transparent) transparent; }
.pf-app .pf-left::-webkit-scrollbar,
.pf-app .pf-right::-webkit-scrollbar { width: 8px; }
.pf-app .pf-left::-webkit-scrollbar-thumb,
.pf-app .pf-right::-webkit-scrollbar-thumb { border-radius: 8px; background: color-mix(in srgb, var(--pf-muted) 40%, transparent); }
.pf-app .pf-left { border-right: 1px solid var(--pf-border); }
.pf-app .pf-right { border-left: 1px solid var(--pf-border); }
.pf-app .pf-stage {
    position: relative;
    display: block;
    min-width: 0;
    padding: 14px;
    overflow: hidden;
    background-color: var(--pf-bg);
    background-image: radial-gradient(color-mix(in srgb, var(--pf-muted) 22%, transparent) 1px, transparent 1px);
    background-size: 18px 18px;
}
.pf-app .pf-device { position: absolute; top: 0; left: 0; box-sizing: content-box !important; border-radius: 8px; background: #fff; box-shadow: var(--pf-shadow); overflow: hidden; transform-origin: 0 0; }
.pf-app .pf-device[data-device="tablet"],
.pf-app .pf-device[data-device="mobile"] { border: 8px solid var(--pf-text); border-radius: 22px; }
.pf-app[data-theme="dark"] .pf-device[data-device="tablet"],
.pf-app[data-theme="dark"] .pf-device[data-device="mobile"] { border-color: #000; }
.pf-app .pf-device[data-device="mobile"] { border-radius: 30px; border-width: 10px; }
.pf-app .pf-frame { display: block; width: 100%; height: 100%; border: 0; background: #fff; }
.pf-app .pf-loading { position: absolute; inset: 0; display: grid; place-items: center; color: var(--pf-muted); pointer-events: none; }
.pf-app.pf-ready .pf-loading { display: none; }
.pf-is-dragging .pf-frame { pointer-events: none; }
.pf-app.pf-previewing .pf-left,
.pf-app.pf-previewing .pf-right { display: none; }
.pf-app.pf-previewing .pf-main { grid-template-columns: minmax(0, 1fr); }

/* ---- Tabs and palette ---- */
.pf-app .pf-tabs { position: sticky; top: 0; z-index: 2; display: flex; gap: 2px; padding: 8px 8px 0; background: var(--pf-panel); border-bottom: 1px solid var(--pf-border); }
.pf-app .pf-tab { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 6px 4px 8px; border-bottom: 2px solid transparent; color: var(--pf-muted); font-size: 11.5px; font-weight: 600; }
.pf-app .pf-tab:hover { color: var(--pf-text); }
.pf-app .pf-tab.pf-on { color: var(--pf-accent); border-bottom-color: var(--pf-accent); }
.pf-app .pf-tab-panel { padding: 12px; }
.pf-app .pf-search { margin-bottom: 12px; }
.pf-app .pf-palette-group + .pf-palette-group { margin-top: 16px; }
.pf-app .pf-group-title { margin-bottom: 8px; color: var(--pf-muted); font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; }
.pf-app .pf-palette-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(72px, 1fr)); gap: 6px; }
.pf-app .pf-block-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 70px;
    padding: 8px 4px;
    border: 1px solid var(--pf-border);
    border-radius: 9px;
    background: var(--pf-panel-2);
    color: var(--pf-text);
    font-size: 11.5px;
    line-height: 1.2;
    text-align: center;
    cursor: grab !important;
    touch-action: manipulation;
    user-select: none;
    transition: border-color .15s, background .15s, transform .15s;
}
.pf-app .pf-block-item .pf-icon { width: 24px; height: 24px; color: var(--pf-accent); }
.pf-app .pf-block-saved { position: relative; }
.pf-app .pf-item-remove { position: absolute; top: 3px; right: 3px; display: none; place-items: center; width: 20px; height: 20px; border-radius: 6px; color: var(--pf-muted); cursor: pointer; }
.pf-app .pf-item-remove .pf-icon { width: 13px !important; height: 13px !important; color: inherit !important; }
.pf-app .pf-block-saved:hover .pf-item-remove,
.pf-app .pf-item-remove:focus-visible { display: grid; }
.pf-app .pf-item-remove:hover { background: color-mix(in srgb, var(--pf-danger) 14%, transparent); color: var(--pf-danger); }
.pf-app .pf-block-item:hover { border-color: var(--pf-accent); background: var(--pf-accent-soft); }
.pf-app .pf-block-item:active { transform: scale(.97); }
.pf-app .pf-muted { color: var(--pf-muted); }
.pf-app .pf-panel-intro { margin-bottom: 12px; font-size: 12px; }
.pf-app .pf-sections { display: grid; gap: 8px; }
.pf-app .pf-section-item { display: flex; align-items: center; gap: 10px; padding: 8px; border: 1px solid var(--pf-border); border-radius: 10px; background: var(--pf-panel-2); text-align: left; cursor: grab !important; user-select: none; touch-action: manipulation; }
.pf-app .pf-section-item:hover { border-color: var(--pf-accent); background: var(--pf-accent-soft); }
.pf-app .pf-section-thumb { flex: none; display: grid; place-items: center; width: 66px; height: 46px; border-radius: 6px; background: var(--pf-panel); color: var(--pf-accent); box-shadow: 0 0 0 1px var(--pf-border) inset; }
.pf-app .pf-section-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.pf-app .pf-section-text small { color: var(--pf-muted); font-size: 11.5px; }
.pf-app .pf-layers,
.pf-app .pf-layers ul { list-style: none; margin: 0; padding: 0; }
.pf-app .pf-layer { display: flex; align-items: center; gap: 6px; width: 100%; min-height: 30px; padding: 4px 8px; border-radius: 6px; text-align: left; }
.pf-app .pf-layer .pf-icon { width: 15px; height: 15px; color: var(--pf-muted); }
.pf-app .pf-layer:hover { background: var(--pf-accent-soft); }
.pf-app .pf-layer.pf-on { background: var(--pf-accent); color: #fff; }
.pf-app .pf-layer.pf-on .pf-icon { color: #fff; }
.pf-app .pf-layer-name { font-weight: 600; white-space: nowrap; }
.pf-app .pf-layer-text { overflow: hidden; color: var(--pf-muted); white-space: nowrap; text-overflow: ellipsis; font-size: 11.5px; }
.pf-app .pf-layer.pf-on .pf-layer-text { color: rgba(255, 255, 255, .75); }

/* ---- Inspector ---- */
.pf-app .pf-inspector-empty { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 32px 20px; color: var(--pf-muted); text-align: center; }
.pf-app .pf-empty-icon { width: 40px !important; height: 40px !important; color: var(--pf-accent); opacity: .6; }
.pf-app .pf-shortcuts { list-style: none !important; display: grid; gap: 6px; margin-top: 8px !important; font-size: 12px; text-align: left; }
.pf-app .pf-shortcuts kbd { display: inline-block; min-width: 58px; padding: 2px 6px; border-radius: 5px; background: var(--pf-bg); color: var(--pf-text); font: 600 11px/1.4 ui-monospace, Menlo, Consolas, monospace; box-shadow: 0 1px 0 var(--pf-border); }
.pf-app .pf-inspector-head { position: sticky; top: 0; z-index: 2; padding: 10px 12px; background: var(--pf-panel); border-bottom: 1px solid var(--pf-border); }
.pf-app .pf-inspector-title { display: flex; align-items: center; gap: 8px; font-size: 14px; }
.pf-app .pf-inspector-title > .pf-icon { color: var(--pf-accent); }
.pf-app .pf-head-actions { display: flex; gap: 2px; margin-left: auto; }
.pf-app .pf-icon-btn { display: grid; place-items: center; width: 28px; height: 28px; border-radius: 7px; color: var(--pf-muted); }
.pf-app .pf-icon-btn .pf-icon { width: 16px; height: 16px; }
.pf-app .pf-icon-btn:hover:not(:disabled) { background: var(--pf-accent-soft); color: var(--pf-accent); }
.pf-app .pf-icon-btn.pf-danger:hover:not(:disabled) { background: color-mix(in srgb, var(--pf-danger) 14%, transparent); color: var(--pf-danger); }
.pf-app .pf-crumbs { display: flex; flex-wrap: wrap; align-items: center; gap: 2px; margin-top: 6px; font-size: 11.5px; }
.pf-app .pf-crumb { padding: 2px 6px; border-radius: 5px; color: var(--pf-muted); }
.pf-app .pf-crumb:hover { background: var(--pf-accent-soft); color: var(--pf-accent); }
.pf-app .pf-crumb.pf-on { color: var(--pf-text); font-weight: 600; }
.pf-app .pf-crumb-sep { color: var(--pf-muted); opacity: .6; }
.pf-app .pf-section { border-bottom: 1px solid var(--pf-border); }
.pf-app .pf-section-title { display: flex; align-items: center; justify-content: space-between; padding: 12px; font-size: 12px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; cursor: pointer; list-style: none; user-select: none; }
.pf-app .pf-section-title::-webkit-details-marker { display: none; }
.pf-app .pf-section-title .pf-caret { width: 14px; height: 14px; color: var(--pf-muted); transition: transform .2s; }
.pf-app .pf-section:not([open]) .pf-caret { transform: rotate(-90deg); }
.pf-app .pf-section-body { display: grid; gap: 14px; padding: 0 12px 16px; }
.pf-app .pf-field { display: grid; gap: 6px; min-width: 0; }
.pf-app .pf-label { color: var(--pf-muted); font-size: 11.5px; font-weight: 600; }
.pf-app .pf-help { color: var(--pf-muted); font-size: 11.5px; line-height: 1.4; }
.pf-app .pf-input {
    width: 100%;
    height: 34px;
    padding: 0 10px;
    border: 1px solid var(--pf-border);
    border-radius: 8px;
    background: var(--pf-panel-2);
    color: var(--pf-text);
    font-size: 13px;
}
.pf-app .pf-input:hover { border-color: color-mix(in srgb, var(--pf-accent) 45%, var(--pf-border)); }
.pf-app .pf-input:focus { outline: 2px solid var(--pf-accent); outline-offset: -1px; border-color: transparent; background: var(--pf-panel); }
.pf-app .pf-input.pf-invalid { border-color: var(--pf-danger); background: color-mix(in srgb, var(--pf-danger) 8%, var(--pf-panel)); }
.pf-app .pf-select { padding-right: 28px; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 256 256'%3E%3Cpath fill='%236b6884' d='m213.66 101.66-80 80a8 8 0 0 1-11.32 0l-80-80a8 8 0 0 1 11.32-11.32L128 164.69l74.34-74.35a8 8 0 0 1 11.32 11.32Z'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 9px center; background-size: 12px; }
.pf-app .pf-select.pf-inherited { color: var(--pf-muted); font-style: italic; }
.pf-app .pf-textarea { height: auto; min-height: 70px; padding: 8px 10px; resize: vertical; line-height: 1.45; }
.pf-app .pf-code { height: auto; padding: 8px 10px; font: 12px/1.5 ui-monospace, Menlo, Consolas, monospace; resize: vertical; white-space: pre; }
.pf-app .pf-two { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.pf-app .pf-segmented { display: flex; padding: 3px; border-radius: 9px; background: var(--pf-bg); }
.pf-app .pf-seg { flex: 1; min-width: 0; height: 28px; padding: 0 6px; border-radius: 6px; color: var(--pf-muted); font-size: 12px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pf-app .pf-seg .pf-icon { width: 16px; height: 16px; }
.pf-app .pf-seg:hover { color: var(--pf-text); }
.pf-app .pf-seg.pf-on { background: var(--pf-panel); color: var(--pf-accent); box-shadow: 0 1px 3px rgba(30, 27, 58, .15); }
.pf-app .pf-field-toggle { gap: 4px; }
.pf-app .pf-switch { display: flex !important; align-items: center; gap: 10px; cursor: pointer; }
.pf-app .pf-switch-input { position: absolute; opacity: 0; width: 1px; height: 1px; }
.pf-app .pf-switch-track { position: relative; flex: none; width: 34px; height: 20px; border-radius: 20px; background: color-mix(in srgb, var(--pf-muted) 35%, transparent); transition: background .15s; }
.pf-app .pf-switch-track::after { content: ""; position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #fff; box-shadow: 0 1px 2px rgba(0, 0, 0, .25); transition: transform .15s; }
.pf-app .pf-switch-input:checked + .pf-switch-track { background: var(--pf-accent); }
.pf-app .pf-switch-input:checked + .pf-switch-track::after { transform: translateX(14px); }
.pf-app .pf-switch-input:focus-visible + .pf-switch-track { outline: 2px solid var(--pf-accent); outline-offset: 2px; }
.pf-app .pf-switch-label { font-weight: 500; }
.pf-app .pf-range-row { display: flex; align-items: center; gap: 8px; }
.pf-app .pf-range { flex: 1; min-width: 0; accent-color: var(--pf-accent); }
.pf-app .pf-number { width: 64px; flex: none; padding: 0 6px; }
.pf-app .pf-unit { color: var(--pf-muted); font-size: 12px; }
.pf-app .pf-color-row { display: flex; align-items: center; gap: 8px; }
.pf-app .pf-color { width: 40px; height: 32px; padding: 2px; border: 1px solid var(--pf-border); border-radius: 8px; background: var(--pf-panel); }
.pf-app .pf-swatches { display: flex; flex-wrap: wrap; gap: 6px; }
.pf-app .pf-swatch { position: relative; width: 24px; height: 24px; padding: 0; border-radius: 50%; box-shadow: 0 0 0 1px rgba(30, 27, 58, .15) inset; cursor: pointer; }
.pf-app .pf-swatch.pf-on { box-shadow: 0 0 0 2px var(--pf-panel), 0 0 0 4px var(--pf-accent); }
.pf-app .pf-swatch-none { background: linear-gradient(135deg, transparent 45%, var(--pf-danger) 45% 55%, transparent 55%), var(--pf-panel); }
.pf-app .pf-swatch-custom { border: 0; background: conic-gradient(#f43f5e, #f59e0b, #84cc16, #06b6d4, #6366f1, #d946ef, #f43f5e); appearance: none; }
.pf-app .pf-swatch-custom::-webkit-color-swatch-wrapper { padding: 0; }
.pf-app .pf-swatch-custom::-webkit-color-swatch { border: 0; border-radius: 50%; opacity: 0; }
.pf-app .pf-swatch-custom.pf-on::-webkit-color-swatch { opacity: 1; }
.pf-app .pf-media { display: flex; gap: 10px; padding: 8px; border: 1px dashed var(--pf-border); border-radius: 10px; }
.pf-app .pf-media.pf-dragover { border-color: var(--pf-accent); background: var(--pf-accent-soft); }
.pf-app .pf-media-preview { flex: none; display: grid; place-items: center; width: 72px; height: 72px; overflow: hidden; border-radius: 8px; background: var(--pf-bg); color: var(--pf-muted); cursor: pointer; }
.pf-app .pf-media-preview img { width: 100%; height: 100%; object-fit: cover; }
.pf-app .pf-media-preview .pf-icon { width: 28px; height: 28px; }
.pf-app .pf-media-side { display: flex; flex: 1; min-width: 0; flex-direction: column; justify-content: center; gap: 6px; }
.pf-app .pf-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; height: 32px; padding: 0 12px; border: 1px solid var(--pf-border); border-radius: 8px; background: var(--pf-panel); font-weight: 600; font-size: 12.5px; white-space: nowrap; }
.pf-app .pf-btn .pf-icon { width: 16px; height: 16px; }
.pf-app .pf-btn:hover { border-color: var(--pf-accent); color: var(--pf-accent); }
.pf-app .pf-btn-primary { border-color: var(--pf-accent); background: var(--pf-accent); color: #fff; }
.pf-app .pf-btn-primary:hover { background: var(--pf-accent-strong); color: #fff; }
.pf-app .pf-link-row { display: grid; gap: 6px; }
.pf-app .pf-check { display: flex !important; align-items: center; gap: 6px; font-size: 12.5px; cursor: pointer; }
.pf-app .pf-check input { accent-color: var(--pf-accent); }
.pf-app .pf-icon-picker { display: grid; gap: 8px; }
.pf-app .pf-icon-tools { display: flex; align-items: center; gap: 8px; }
.pf-app .pf-icon-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(34px, 1fr)); gap: 4px; max-height: 184px; overflow: auto; padding: 2px; scrollbar-width: thin; }
.pf-app .pf-icon-choice { display: grid; place-items: center; height: 34px; border-radius: 7px; color: var(--pf-text); }
.pf-app .pf-icon-choice:hover { background: var(--pf-accent-soft); color: var(--pf-accent); }
.pf-app .pf-icon-choice.pf-on { background: var(--pf-accent); color: #fff; }
.pf-app .pf-columns { display: grid; gap: 6px; }
.pf-app .pf-column-bp { display: grid; grid-template-columns: 44px 1fr; align-items: center; gap: 8px; }
.pf-app .pf-bp { padding: 3px 0; border-radius: 6px; background: var(--pf-bg); color: var(--pf-muted); font-size: 11px; font-weight: 700; text-align: center; }
.pf-app .pf-bp.pf-on { background: var(--pf-accent); color: #fff; }
.pf-app .pf-actions-row { display: flex; flex-wrap: wrap; gap: 6px; }
.pf-app .pf-items { display: grid; gap: 10px; }
.pf-app .pf-item { display: grid; gap: 8px; padding: 10px; border: 1px solid var(--pf-border); border-radius: 10px; background: var(--pf-panel-2); }
.pf-app .pf-item-inline { display: flex; align-items: center; gap: 4px; padding: 6px; }
.pf-app .pf-item-head { display: flex; align-items: center; justify-content: space-between; }
.pf-app .pf-spacing { font-size: 11px; }
.pf-app .pf-spacing-margin,
.pf-app .pf-spacing-padding { position: relative; display: grid; grid-template-areas: ". t ." "s c e" ". b ."; grid-template-columns: 44px 1fr 44px; align-items: center; justify-items: center; gap: 3px; padding: 18px 4px 5px; border: 1px dashed color-mix(in srgb, var(--pf-muted) 50%, transparent); border-radius: 8px; }
.pf-app .pf-spacing-margin { background: color-mix(in srgb, #f59e0b 7%, transparent); }
.pf-app .pf-spacing-padding { grid-area: c; width: 100%; background: color-mix(in srgb, #10b981 8%, var(--pf-panel)); }
.pf-app .pf-spacing-label { position: absolute; top: 3px; left: 7px; color: var(--pf-muted); font-size: 10px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.pf-app .pf-sp-t { grid-area: t; }
.pf-app .pf-sp-b { grid-area: b; }
.pf-app .pf-sp-s { grid-area: s; }
.pf-app .pf-sp-e { grid-area: e; }
.pf-app .pf-spacing-core { grid-area: c; width: 100%; min-width: 30px; height: 22px; border-radius: 4px; background: color-mix(in srgb, var(--pf-accent) 18%, transparent); }
.pf-app .pf-space-select { width: 44px; height: 24px; padding: 0 2px; border: 1px solid var(--pf-border); border-radius: 6px; background: var(--pf-panel); text-align: center; font-size: 12px; }
.pf-app .pf-visibility { display: grid; grid-template-columns: repeat(6, 1fr); gap: 4px; }
.pf-app .pf-visibility-item { display: flex !important; flex-direction: column; align-items: center; gap: 4px; padding: 6px 0; border-radius: 8px; background: var(--pf-bg); font-size: 11px; font-weight: 700; cursor: pointer; }
.pf-app .pf-visibility-item input { accent-color: var(--pf-accent); }
.pf-app .pf-code-field { display: grid; gap: 8px; }

/* ---- Dialog, notices, drag ghost ---- */
.pf-app .pf-dialog { width: min(920px, calc(100vw - 32px)); max-height: calc(100vh - 48px); padding: 16px; border: 0; border-radius: 14px; background: var(--pf-panel); color: var(--pf-text); box-shadow: 0 24px 60px rgba(20, 16, 60, .35); }
.pf-app .pf-dialog-small { width: min(420px, calc(100vw - 32px)); }
.pf-app .pf-dialog-form { display: grid; gap: 8px; }
.pf-app .pf-dialog::backdrop { background: rgba(20, 16, 50, .45); backdrop-filter: blur(2px); }
.pf-app .pf-dialog-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px; font-size: 15px; }
.pf-app .pf-dialog .pf-muted { margin-bottom: 10px; font-size: 12.5px; }
.pf-app .pf-code-editor { display: block; width: 100%; height: min(56vh, 520px); padding: 12px; border: 1px solid var(--pf-border); border-radius: 10px; background: #15132b; color: #e9e7ff; font: 12.5px/1.6 ui-monospace, Menlo, Consolas, monospace; white-space: pre; resize: vertical; tab-size: 4; }
.pf-app .pf-dialog-actions { display: flex; align-items: center; gap: 8px; margin-top: 12px; }
.pf-app .pf-spacer { flex: 1; }
.pf-app .pf-toasts { position: absolute; left: 50%; bottom: 16px; z-index: 30; display: grid; gap: 8px; transform: translateX(-50%); pointer-events: none; }
.pf-app .pf-toast { max-width: min(460px, 90cqw); padding: 10px 16px; border-radius: 10px; background: var(--pf-text); color: var(--pf-panel); box-shadow: var(--pf-shadow); font-weight: 500; text-align: center; animation: pf-in .2s ease; }
.pf-app .pf-toast-action { display: flex; align-items: center; gap: 10px; pointer-events: auto; text-align: left; }
.pf-app .pf-toast-btn { padding: 4px 10px; border-radius: 7px; background: var(--pf-accent); color: #fff; font-weight: 600; white-space: nowrap; }
.pf-app .pf-toast-close { display: grid; place-items: center; width: 24px; height: 24px; border-radius: 6px; opacity: .7; }
.pf-app .pf-toast-close:hover { opacity: 1; background: rgba(255, 255, 255, .12); }
.pf-app .pf-toast-success { background: var(--pf-success); color: #fff; }
.pf-app .pf-toast-error { background: var(--pf-danger); color: #fff; }
.pf-app .pf-toast-warning { background: var(--pf-warning); color: #fff; }
.pf-app .pf-toast.pf-out { opacity: 0; transform: translateY(6px); transition: .3s; }
@keyframes pf-in { from { opacity: 0; transform: translateY(6px); } }
.pf-ghost {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 2147483600;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 9px;
    background: #6d5efc;
    color: #fff;
    font: 600 13px/1.2 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    box-shadow: 0 10px 30px rgba(60, 40, 200, .35);
    pointer-events: none;
    white-space: nowrap;
}
.pf-ghost .pf-icon { display: inline-grid; width: 18px; height: 18px; }
.pf-ghost .pf-icon svg { width: 100%; height: 100%; }
.pf-is-dragging { cursor: grabbing !important; user-select: none; }

/* ---- Narrow containers: side panels become drawers ---- */
.pf-app .pf-scrim { display: none; }

/* ---- Medium containers: one panel, the settings in a tab ---- */
.pf-app:not(.pf-medium) .pf-tab[data-tab="settings"] { display: none; }
.pf-app .pf-tab-panel[data-tab="settings"] { padding: 0; }
.pf-app.pf-medium { --pf-left: 300px; }
.pf-app.pf-medium .pf-main { grid-template-columns: var(--pf-left) minmax(0, 1fr); }
.pf-app.pf-medium.pf-hide-left .pf-main { grid-template-columns: minmax(0, 1fr); }
.pf-app.pf-medium.pf-hide-left .pf-left { display: none; }
.pf-app.pf-medium .pf-right { overflow: visible; border: 0; background: none; }
.pf-app.pf-medium .pf-inspector-head { position: static; }
.pf-app.pf-medium .pf-tab { padding-left: 2px; padding-right: 2px; font-size: 11px; }
.pf-app.pf-narrow .pf-main { grid-template-columns: minmax(0, 1fr); }
.pf-app.pf-narrow .pf-left,
.pf-app.pf-narrow .pf-right {
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 20;
    width: min(320px, 88%);
    box-shadow: var(--pf-shadow);
    transition: transform .22s ease, visibility .22s;
    visibility: hidden;
}
.pf-app.pf-narrow .pf-left { left: 0; transform: translateX(-100%); }
.pf-app.pf-narrow .pf-right { right: 0; transform: translateX(100%); }
.pf-app.pf-narrow.pf-open-left .pf-left,
.pf-app.pf-narrow.pf-open-right .pf-right { transform: none; visibility: visible; }
.pf-app.pf-narrow.pf-open-left .pf-scrim,
.pf-app.pf-narrow.pf-open-right .pf-scrim { display: block; position: absolute; inset: 0; z-index: 15; background: rgba(20, 16, 50, .3); }
.pf-app.pf-narrow .pf-stage { padding: 8px; }
.pf-app.pf-tiny .pf-topbar { flex-wrap: wrap; justify-content: center; }
.pf-app.pf-tiny [data-tool="guides"],
.pf-app.pf-tiny [data-tool="copy"] { display: none; }
.pf-app.pf-tiny .pf-device[data-device="tablet"],
.pf-app.pf-tiny .pf-device[data-device="mobile"] { border-width: 0; border-radius: 8px; }

/* The preview gives all the room to the canvas, whatever the layout. */
.pf-app.pf-previewing .pf-main { grid-template-columns: minmax(0, 1fr) !important; }
.pf-app.pf-previewing .pf-left,
.pf-app.pf-previewing .pf-right { display: none !important; }
